import React, { useState, useEffect } from 'react';
import { 
  UserX, 
  Shield, 
  Mail, 
  Phone, 
  Calendar,
  Check,
  X as XIcon,
  RefreshCw,
  Trash2
} from 'lucide-react';
import {
  Layout,
  SearchBar,
  FilterDropdown,
  ConfirmDialog,
  LoadingSpinner,
  Alert,
  EmptyState,
  FilterOption
} from '../../components';
import { useDebounce, useFilters } from '../../hooks';
import { userService } from '../../services';
import { User } from '../../types';
import { logger } from '../../utils';
import UserDetailView from './UserDetailView';

export const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<User[] | null>(null);

  const { filters, setFilter, clearAllFilters, hasActiveFilters } = useFilters();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userService.getAll();
      setUsers(data);
    } catch (error) {
      logger.apiError('/admin/users', error, { component: 'UsersPage', action: 'fetchUsers' });
      setError('Failed to fetch users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (!deleteConfirm || deleteConfirm.length === 0) return;
    
    try {
      await Promise.all(
        deleteConfirm.map(user => userService.delete(user.id))
      );
      setDeleteConfirm(null);
      setSelectedUsers(new Set());
      fetchUsers();
    } catch (error) {
      logger.apiError('/admin/users/delete', error, { component: 'UsersPage', action: 'deleteUsers' });
      setError('Failed to delete some users');
      setDeleteConfirm(null);
    }
  };

  const toggleUserSelection = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    clearAllFilters();
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.email?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      user.phone?.includes(debouncedSearch) ||
      user.name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      user.id.includes(debouncedSearch);
    
    const matchesVerified = 
      !filters.verified || filters.verified === 'all' ||
      (filters.verified === 'verified' && user.isVerified) ||
      (filters.verified === 'unverified' && !user.isVerified);

    return matchesSearch && matchesVerified;
  });

  const verifiedOptions: FilterOption[] = [
    { label: 'All Users', value: 'all' },
    { label: 'Verified Only', value: 'verified' },
    { label: 'Unverified Only', value: 'unverified' },
  ];

  if (selectedUser) {
    return (
      <Layout currentPage="users">
        <UserDetailView
          user={selectedUser}
          onBack={() => setSelectedUser(null)}
          onUpdate={(updated) => {
            setUsers(users.map(u => u.id === updated.id ? updated : u));
            setSelectedUser(null);
          }}
        />
      </Layout>
    );
  }

  return (
    <Layout currentPage="users">
      <div className="px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Header */}
        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
            <p className="mt-2 text-sm text-gray-700">
              Manage user accounts, verify users, and reset passwords ({users.length} total)
            </p>
          </div>
          <button
            onClick={fetchUsers}
            disabled={loading}
            className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4">
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search by email, phone, name, or ID..."
                className="flex-1"
              />

              <FilterDropdown
                label="Filter"
                value={filters.verified || 'all'}
                options={verifiedOptions}
                onChange={(value) => setFilter('verified', value)}
                onClear={() => setFilter('verified', 'all')}
                className="sm:w-48"
              />
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedUsers.size > 0 && (
            <div className="px-4 py-3 bg-orange-50 border-b border-orange-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-orange-900">
                  {selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={() => setDeleteConfirm(users.filter(u => selectedUsers.has(u.id)))}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Delete Selected
                </button>
              </div>
            </div>
          )}
        </div>

        {error && (
          <Alert
            variant="error"
            message={error}
            onClose={() => setError(null)}
          />
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Shield className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                    <dd className="text-lg font-semibold text-gray-900">{users.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Check className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Verified</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {users.filter(u => u.isVerified).length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <XIcon className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Unverified</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {users.filter(u => !u.isVerified).length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {loading ? (
            <LoadingSpinner className="py-12" message="Loading users..." />
          ) : filteredUsers.length === 0 ? (
            <EmptyState
              icon={<UserX className="h-12 w-12 text-gray-400" />}
              title="No users found"
              message={
                debouncedSearch || hasActiveFilters
                  ? 'Try adjusting your search or filters'
                  : 'No users in the system yet'
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedUsers.size === filteredUsers.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
                          } else {
                            setSelectedUsers(new Set());
                          }
                        }}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Auth
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr 
                      key={user.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedUser(user)}
                    >
                      <td 
                        className="px-6 py-4 whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold">
                            {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '?'}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.name || 'Unnamed User'}
                            </div>
                            <div className="text-sm text-gray-500 font-mono text-xs">
                              {user.id.slice(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.email && (
                          <div className="flex items-center text-sm text-gray-900">
                            <Mail className="h-4 w-4 mr-2 text-gray-400" />
                            {user.email}
                          </div>
                        )}
                        {user.phone && (
                          <div className="flex items-center text-sm text-gray-500 mt-1">
                            <Phone className="h-4 w-4 mr-2 text-gray-400" />
                            {user.phone}
                          </div>
                        )}
                        {!user.email && !user.phone && (
                          <span className="text-sm text-gray-400">No contact info</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.isVerified
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {user.isVerified ? (
                              <>
                                <Check className="h-3 w-3 mr-1" />
                                Verified
                              </>
                            ) : (
                              <>
                                <XIcon className="h-3 w-3 mr-1" />
                                Unverified
                              </>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className="capitalize">{user.authProvider}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                          {new Date(user.createdAt).toLocaleDateString()}
                        </div>
                        {user.lastLoginAt && (
                          <div className="text-xs text-gray-400 mt-1">
                            Last: {new Date(user.lastLoginAt).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedUser(user);
                          }}
                          className="text-orange-600 hover:text-orange-900"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDeleteSelected}
        title={`Delete ${deleteConfirm?.length || 0} User${deleteConfirm && deleteConfirm.length !== 1 ? 's' : ''}`}
        message={`Are you sure you want to delete ${deleteConfirm?.length || 0} user${deleteConfirm && deleteConfirm.length !== 1 ? 's' : ''}? This action cannot be undone.`}
        confirmText="Delete"
        confirmButtonVariant="danger"
      />
    </Layout>
  );
};

export default UsersPage;
